"use client"

import React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { 
  Home,
  Users, 
  Calendar,
  Settings,
  TrendingUp,
  PieChart,
  Activity,
  Search,
  X,
  Bot
} from "lucide-react"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { useAuth } from "../hooks/useAuth"
import { cn } from "../lib/utils"
import logoImage from "../assets/images/5cpa.png"

const navigationData = {
  navMain: [
    {
      title: "Dashboard",
      url: "/",
      icon: Home,
    },
    {
      title: "Eventos",
      url: "/eventos",
      icon: Calendar,
    },
    {
      title: "Disponibilidade",
      url: "/disponibilidade",
      icon: Users,
    },
  ],
  navSecondary: [
    {
      title: "Configurações",
      url: "/configuracoes",
      icon: Settings,
    },
    {
      title: "IA",
      url: "/ai",
      icon: Bot,
    },
    {
      title: "Relatórios",
      url: "/relatorios",
      icon: PieChart,
    },
    {
      title: "Logs",
      url: "/logs",
      icon: Activity,
    },
  ],
  quickActions: [
    {
      title: "Analytics",
      url: "/",
      icon: TrendingUp,
    },
    {
      title: "Relatórios",
      url: "/relatorios",
      icon: PieChart,
    },
    {
      title: "Atividade",
      url: "/logs",
      icon: Activity,
    },
  ],
}

export function Sidebar({ isOpen, isMobile, onClose }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const isActiveRoute = (url) => {
    if (url === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(url)
  }

  const handleNavigation = (url) => {
    if (url && url !== '#') {
      navigate(url)
      // Fechar sidebar no mobile após navegação
      if (isMobile && onClose) {
        onClose()
      }
    }
  }

  return (
    <>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside className="flex h-full w-64 flex-col border-r bg-background">
          <SidebarContent 
            user={user}
            isActiveRoute={isActiveRoute}
            handleNavigation={handleNavigation}
          />
        </aside>
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <aside className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform bg-background shadow-xl transition-transform duration-300 ease-in-out border-r",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          {/* Mobile close button */}
          <div className="flex justify-end p-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <SidebarContent 
            user={user}
            isActiveRoute={isActiveRoute}
            handleNavigation={handleNavigation}
          />
        </aside>
      )}
    </>
  )
}

// Componente separado para o conteúdo do sidebar
function SidebarContent({ user, isActiveRoute, handleNavigation }) {
  return (
    <>
      {/* Header */}
      <div className="flex h-16 items-center border-b px-4">
        <div 
          className="flex items-center gap-2 transition-all cursor-pointer"
          onClick={() => handleNavigation('/')}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border overflow-hidden">
            <img src={logoImage} alt="5ºCPA Logo" className="h-6 w-6 object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold">RAS-5ºCPA</span>
            <span className="text-xs text-muted-foreground">v2.0</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            className="pl-8"
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2 flex flex-col">
        {/* Main Navigation */}
        <div className="space-y-1 px-2">
          {navigationData.navMain.map((item) => {
            const isItemActive = isActiveRoute(item.url)

            return (
              <Button
                key={item.title}
                variant={isItemActive ? "secondary" : "ghost"}
                className="w-full justify-start gap-2 h-10"
                onClick={() => handleNavigation(item.url)}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{item.title}</span>
              </Button>
            )
          })}
        </div>

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* Secondary Navigation */}
        <div className="mb-4">
          <div className="px-4 mb-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Sistema
            </h4>
          </div>
          <div className="space-y-1 px-2">
            {navigationData.navSecondary.map((item) => {
              const isItemActive = isActiveRoute(item.url)

              return (
                <Button
                  key={item.title}
                  variant={isItemActive ? "secondary" : "ghost"}
                  className="w-full justify-start gap-2 h-10"
                  onClick={() => handleNavigation(item.url)}
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left truncate">{item.title}</span>
                </Button>
              )
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-4">
          <div className="px-4 mb-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Ações Rápidas
            </h4>
          </div>
          <div className="space-y-1 px-2">
            {navigationData.quickActions.map((item) => (
              <Button
                key={item.title}
                variant={isActiveRoute(item.url) ? "secondary" : "ghost"}
                size="sm"
                className="w-full justify-start gap-2 h-8"
                onClick={() => handleNavigation(item.url)}
              >
                <item.icon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{item.title}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* User Section */}
      <div className="border-t p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src="https://github.com/shadcn.png" />
            <AvatarFallback>
              <Users className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-1 flex-col min-w-0">
            <span className="text-sm font-medium truncate">{user?.nome || 'Usuário'}</span>
            <span className="text-xs text-muted-foreground truncate">{user?.email || 'usuario@ras.com'}</span>
          </div>
        </div>
      </div>
    </>
  )
}
